// src/components/appInitialization/AppInitializer.tsx
import React, { useEffect, useState, useRef } from 'react';
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { selectAllDocuments } from "../../redux/api/documents/documentsSelectors";
import { initializeApp } from "../../redux/api/app/appSlice";
import { fetchDocumentDetails } from "../../redux/api/documents/documentsThunks";
import Spinner from "../common/Spinner";
import { Outlet } from "react-router-dom";
import { notification } from 'antd';

const AppInitializer: React.FC = () => {
    const dispatch = useAppDispatch();
    const documents = useAppSelector(selectAllDocuments);
    const [initialized, setInitialized] = useState<boolean>(false);

    // Ref для отслеживания уже запрошенных документов
    const requestedDocsRef = useRef(new Set<string>());
    // Ref для отслеживания документов, по которым уже выведено сообщение в консоль
    const loggedDocsRef = useRef(new Set<string>());

    // Шаг 1: Инициализация приложения
    useEffect(() => {
        dispatch(initializeApp())
            .unwrap()
            .then(() => {
                setInitialized(true);
            })
            .catch((error) => {
                console.error('Ошибка инициализации приложения:', error);
                notification.error({
                    message: 'Ошибка инициализации приложения',
                    description: error.message || String(error),
                });
            });
    }, [dispatch]);

    // Шаг 2: После инициализации для каждого документа запускаем fetchDocumentDetails или логируем путь
    useEffect(() => {
        if (initialized) {
            Object.values(documents).forEach((doc) => {
                if (!doc.detailsLoaded) {
                    // Если ещё не запрошено – инициируем загрузку деталей
                    if (!requestedDocsRef.current.has(doc.id)) {
                        dispatch(fetchDocumentDetails(doc.id))
                            .unwrap()
                            .catch((error) => {
                                notification.error({
                                    message: 'Ошибка загрузки документа',
                                    description: `Не удалось загрузить документ с ID: ${doc.id}. Ошибка: ${error.message || String(error)}`,
                                });
                            });
                        requestedDocsRef.current.add(doc.id);
                    }
                } else {
                    // Если документ уже загружен – логируем путь, но только один раз
                    if (!loggedDocsRef.current.has(doc.id)) {
                        loggedDocsRef.current.add(doc.id);
                    }
                }
            });
        }
    }, [initialized, documents, dispatch]);

    // Пока приложение не инициализировано, показываем спиннер
    if (!initialized) {
        return <Spinner />;
    }

    // После инициализации рендерим дочерние маршруты
    return <Outlet />;
};

export default AppInitializer;
